import { useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { PanelFrame } from '../panel-frame.js';
import type { PanelId, TabId } from '../store.js';

// Help — a tabbed reference panel (same chrome as Methodology): what the panels
// are and how they connect, the dock/link/tab workflows, keyboard shortcuts, the
// privacy stance on ward lists, and how to reach support.

function H({ children }: { children: React.ReactNode }) {
  return <div className="hlp-h">{children}</div>;
}
function P({ children }: { children: React.ReactNode }) {
  return <p className="hlp-p">{children}</p>;
}
function Note({ children }: { children: React.ReactNode }) {
  return <div className="hlp-note">{children}</div>;
}
function Defn({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div className="hlp-defn">
      <span className="hlp-defn-t">{term}</span>
      <span className="hlp-defn-d">{children}</span>
    </div>
  );
}

// --- Panels -----------------------------------------------------------------
function PanelsTab() {
  return (
    <div className="hlp-doc">
      <P>
        The workspace is a grid of <b>panels</b> you arrange yourself. Each panel answers one
        question; together they run the origination funnel from <b>where to look</b> down to{' '}
        <b>which company to act on</b>.
      </P>

      <H>Discovery</H>
      <Defn term="Triage">
        the master screener — every operator across the 11 markets, filtered and sorted, with the
        revenue estimate and its confidence on each row.
      </Defn>
      <Defn term="Referrals">
        a randomised shortlist of the most acquisition-attractive operators by the{' '}
        <b>Corryk Origination Signal</b> — a discovery surface, not a leaderboard.
      </Defn>
      <Defn term="Record">
        the single-company dossier — vitals, profile, ownership, the signals behind it.
      </Defn>
      <Defn term="Ward">a saved list of operators you are tracking.</Defn>

      <H>Market &amp; workflow</H>
      <Defn term="Indications">
        the region × sub-sector heatmap — the <b>Corryk Opportunity Index</b>, market size and
        fragmentation: where to build.
      </Defn>
      <Defn term="Prevalence">
        the consolidator landscape — who is rolling up, their footprint, and the <b>white space</b>{' '}
        (territory with independents but no consolidator).
      </Defn>
      <Defn term="Catchment / Census">
        draw a drive-time catchment around a company and read the demographics, demand and clinician
        supply inside it.
      </Defn>
      <Defn term="Pressure">
        policy and operating-pressure news by category, market and severity.
      </Defn>
      <Defn term="Map / Methodology / Help">
        the facilities map, the technical reference, and this guide.
      </Defn>

      <H>How they work together</H>
      <P>
        Panels share a selection through <b>link channels</b> (next section): pick a company in
        Triage or Referrals and a linked Record, Catchment and Census all follow it. The chain runs{' '}
        <b>Indications → Prevalence → Triage/Referrals → Record → Catchment</b> — from the map down
        to a single building.
      </P>
    </div>
  );
}

// --- Bespoke workflows ------------------------------------------------------
function WorkflowsTab() {
  return (
    <div className="hlp-doc">
      <P>
        The workspace behaves like a trading terminal — you build the layout, and panels talk to
        each other through channels.
      </P>

      <H>View tabs</H>
      <P>
        The tabs along the top (Landing, Workspace, Market…) are <b>saved layouts</b>. Each holds
        its own set of panels and arrangement. Click <b>+</b> to open a fresh, empty tab; switch
        tabs to switch context without losing the others.
      </P>

      <H>Docking &amp; undocking</H>
      <Defn term="Rearrange">
        drag a panel by its title bar to split, stack or reorder panels within a tab.
      </Defn>
      <Defn term="Undock (float)">
        drag a panel out, or use its <b>☰</b> menu → Undock, to pop it into a free-floating window
        you can move and resize over the grid.
      </Defn>
      <Defn term="Dock back">
        drag a floating panel onto the grid (or ☰ → Dock) to snap it back in.
      </Defn>

      <H>Link channels</H>
      <P>
        Each panel&apos;s <b>☰</b> menu has a coloured channel indicator. Panels on the{' '}
        <b>same channel</b> share one selected company: choose an operator in one and every linked
        panel updates. Open the menu → <b>Link</b> to move a panel onto a channel —{' '}
        <b>Claret, Blue, Teal, Terracotta</b> — or <b>Off</b> to pin it so it stops following. Run
        two independent investigations side by side by putting each on its own channel.
      </P>

      <Note>
        Tip: open two Records on different channels to compare two companies, each fed by its own
        Triage selection.
      </Note>
    </div>
  );
}

// --- Shortcuts --------------------------------------------------------------
function ShortcutsTab() {
  return (
    <div className="hlp-doc">
      <H>Keyboard</H>
      <Defn term="⌘K / Ctrl K">
        open the command palette — search panels, entities and actions.
      </Defn>
      <Defn term="⌘T / Ctrl T">open a new view tab.</Defn>
      <Defn term="⌘S / Ctrl S">save the current workspace layout.</Defn>
      <Defn term="Esc">close an open overlay, or clear the current selection.</Defn>
      <Defn term="?">toggle the keyboard-shortcuts overlay.</Defn>

      <H>Mouse</H>
      <Defn term="Double-click a row">open that company&apos;s Record.</Defn>
      <Defn term="Right-click a row">quick actions — add to a ward, open the Record, copy.</Defn>
      <Defn term="Drag a column header">reorder columns; drag its right edge to resize.</Defn>
      <Defn term="Drag a panel title">dock, split or float the panel.</Defn>

      <Note>
        The command palette (⌘K) is the fastest route to anything — it lists every panel and action.
      </Note>
    </div>
  );
}

// --- Privacy ----------------------------------------------------------------
function PrivacyTab() {
  return (
    <div className="hlp-doc">
      <P>
        Your wards are <b>yours</b>. We do not record, store or report{' '}
        <i>which user shortlisted which company</i>. The only thing retained is an{' '}
        <b>anonymous, no-names list of the entities</b> added to wards across the platform — never
        tied back to you, your firm or your account.
      </P>

      <H>Why we keep the anonymised list</H>
      <P>
        Periodically we cross-reference that list against <b>actual M&amp;A transactions</b>{' '}
        observed in the market. Where the operators users flagged turn out to transact, that signal
        helps <b>train and improve the model</b> behind the <b>Corryk Origination Signal</b> — so
        the platform&apos;s sense of what makes a good target keeps sharpening from real outcomes.
      </P>

      <H>What this means</H>
      <Defn term="No attribution">
        no link is ever stored between a user and the entities they shortlisted.
      </Defn>
      <Defn term="Entities only">
        the retained list is operator identities, on an aggregate, no-names basis.
      </Defn>
      <Defn term="Purpose-bound">
        used only to back-test against real deals and supplement the Origination Signal.
      </Defn>

      <Note>
        In short: the collective wisdom of what gets shortlisted makes the model better, while who
        shortlisted what stays private.
      </Note>
    </div>
  );
}

// --- Support ----------------------------------------------------------------
function SupportTab() {
  return (
    <div className="hlp-doc">
      <H>Contact support</H>
      <P>
        Questions, data issues, feature requests or anything that looks wrong — email the team and
        we&apos;ll get back to you.
      </P>
      <P>
        <a
          href="mailto:support@corryk.com"
          style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}
        >
          support@corryk.com
        </a>
      </P>
      <Note>
        When reporting a data problem, the company name (or registry id) and the panel you saw it in
        help us trace it fastest.
      </Note>
    </div>
  );
}

const TABS: Array<{ id: string; label: string; render: () => React.ReactNode }> = [
  { id: 'panels', label: 'Panels', render: PanelsTab },
  { id: 'workflows', label: 'Bespoke workflows', render: WorkflowsTab },
  { id: 'shortcuts', label: 'Shortcuts', render: ShortcutsTab },
  { id: 'privacy', label: 'Privacy', render: PrivacyTab },
  { id: 'support', label: 'Support', render: SupportTab },
];

export function HelpPanel({ tabId, panelId }: { tabId: TabId; panelId: PanelId }) {
  const [tab, setTab] = useState('panels');
  return (
    <PanelFrame tabId={tabId} panelId={panelId}>
      <div className="hlp-root">
        <style>{HLP_CSS}</style>
        <Tabs.Root value={tab} onValueChange={setTab} className="hlp-tabs">
          <Tabs.List className="hlp-tablist">
            {TABS.map((t) => (
              <Tabs.Trigger key={t.id} value={t.id} className="hlp-tab">
                {t.label}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
          {TABS.map((t) => (
            <Tabs.Content key={t.id} value={t.id} className="hlp-body">
              {t.render()}
            </Tabs.Content>
          ))}
        </Tabs.Root>
      </div>
    </PanelFrame>
  );
}

const HLP_CSS = `
.hlp-root { display:flex; flex-direction:column; height:100%; font-size:var(--t-body); }
.hlp-tabs { display:flex; flex-direction:column; min-height:0; flex:1; }
.hlp-tablist { display:flex; gap:16px; padding:8px var(--s-3) 0; border-bottom:1px solid var(--hairline); flex-wrap:wrap; }
.hlp-tab { font:inherit; font-size:var(--t-body); font-weight:500; color:var(--text-tertiary); background:none; border:none; border-bottom:2px solid transparent; padding:4px 0 6px; cursor:pointer; white-space:nowrap; }
.hlp-tab[data-state="active"] { color:var(--text-primary); font-weight:600; border-bottom-color:var(--accent); }
.hlp-body { flex:1; overflow:auto; padding:var(--s-4) var(--s-3); min-height:0; }
.hlp-doc { max-width:720px; }
.hlp-h { font-size:var(--t-meta); text-transform:uppercase; letter-spacing:.06em; color:var(--text-secondary); font-weight:700; margin:20px 0 7px; border-top:2px solid var(--text-primary); padding-top:7px; }
.hlp-doc > .hlp-h:first-child { margin-top:0; }
.hlp-p { margin:0 0 9px; line-height:1.55; color:var(--text-secondary); }
.hlp-p b { color:var(--text-primary); font-weight:600; }
.hlp-defn { display:grid; grid-template-columns:150px 1fr; gap:10px; padding:6px 0; border-bottom:1px solid var(--hairline); line-height:1.5; }
.hlp-defn:last-of-type { border-bottom:none; }
.hlp-defn-t { font-weight:600; color:var(--text-primary); }
.hlp-defn-d { color:var(--text-secondary); }
.hlp-note { margin-top:14px; padding:9px 11px; background:var(--menu-surface); border:1px solid var(--hairline); border-radius:var(--radius-sm); font-size:var(--t-meta); color:var(--text-tertiary); line-height:1.5; }
`;
